/**
 * Native X (Twitter) Website Tag for SiteGlue paid media.
 * Pixel ID from Ads Manager → Events Manager (SiteGlue AI).
 *
 * GTM-synced events alone have been unreliable (Inactive / 0 events).
 * This loads `uwt.js` directly on siteglue.ai and SiteGlue-branded app sessions.
 *
 * Event IDs use format `tw-{pixelId}-{tagId}` from Events Manager Tag ID column.
 * Override via NEXT_PUBLIC_SITEGLUE_X_* env vars when X regenerates IDs.
 */

import {
  MARKETING_BRAND_QUERY_KEY,
  MARKETING_BRAND_SESSION_KEY,
  MARKETING_BRAND_SITEGLUE,
} from "~/lib/marketing-brand";

export const SITEGLUE_X_PIXEL_ID =
  process.env.NEXT_PUBLIC_SITEGLUE_X_PIXEL_ID?.trim() || "re0th";

/** GTM Page view — Tag ID 13vg9n (Conversion Diagnostics). */
export const SITEGLUE_X_EVENT_PAGE_VIEW =
  process.env.NEXT_PUBLIC_SITEGLUE_X_EVENT_PAGE_VIEW?.trim() ||
  `tw-${SITEGLUE_X_PIXEL_ID}-13vg9n`;

/**
 * GTM Sign up (Lead). Set NEXT_PUBLIC_SITEGLUE_X_EVENT_SIGN_UP=tw-re0th-XXXX
 * from Events Manager → GTM Sign up → Tag ID if the default is wrong.
 */
export const SITEGLUE_X_EVENT_SIGN_UP =
  process.env.NEXT_PUBLIC_SITEGLUE_X_EVENT_SIGN_UP?.trim() || "";

/**
 * GTM Purchase — optional. Set from Events Manager Tag ID for purchase events.
 * We also map agent_hired → Lead when purchase ID is unset.
 */
export const SITEGLUE_X_EVENT_PURCHASE =
  process.env.NEXT_PUBLIC_SITEGLUE_X_EVENT_PURCHASE?.trim() || "";

const UWT_SRC = "https://static.ads-twitter.com/uwt.js";

type TwqFn = (...args: unknown[]) => void;

type TwqWindow = Window & {
  twq?: TwqFn & { exe?: TwqFn; queue?: unknown[]; version?: string };
  __siteglueXPixelConfigured?: boolean;
  __siteglueXPixelLoading?: Promise<void>;
};

function getTwqWindow(): TwqWindow | null {
  if (typeof window === "undefined") return null;
  return window as TwqWindow;
}

/** True on siteglue.ai hosts or when the app session is branded SiteGlue. */
export function shouldLoadSiteGlueXPixel(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  if (host.includes("siteglue.ai")) return true;
  if (document.documentElement.getAttribute("data-marketing-brand") === "siteglue") {
    return true;
  }
  try {
    if (sessionStorage.getItem(MARKETING_BRAND_SESSION_KEY) === MARKETING_BRAND_SITEGLUE) {
      return true;
    }
  } catch {
    /* ignore */
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(MARKETING_BRAND_QUERY_KEY)?.toLowerCase() === MARKETING_BRAND_SITEGLUE) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function ensureTwqStub(w: TwqWindow): TwqFn {
  if (typeof w.twq === "function") return w.twq;
  const twq = function twqStub(this: unknown, ...args: unknown[]) {
    const fn = twq as TwqFn & { exe?: TwqFn; queue: unknown[] };
    if (fn.exe) {
      fn.exe.apply(twq, args);
    } else {
      fn.queue.push(args);
    }
  } as TwqFn & { exe?: TwqFn; queue: unknown[]; version: string };
  twq.version = "1.1";
  twq.queue = [];
  w.twq = twq;
  return twq;
}

/** Inject uwt.js once and config the SiteGlue pixel (fires Site Visit / PageView). */
export function ensureSiteGlueXPixel(): Promise<void> {
  const w = getTwqWindow();
  if (!w || !SITEGLUE_X_PIXEL_ID) return Promise.resolve();
  if (!shouldLoadSiteGlueXPixel()) return Promise.resolve();

  if (w.__siteglueXPixelConfigured) return Promise.resolve();
  if (w.__siteglueXPixelLoading) return w.__siteglueXPixelLoading;

  w.__siteglueXPixelLoading = new Promise<void>((resolve) => {
    try {
      const twq = ensureTwqStub(w);
      const existing = document.querySelector(`script[src="${UWT_SRC}"]`);
      const finish = () => {
        if (!w.__siteglueXPixelConfigured) {
          twq("config", SITEGLUE_X_PIXEL_ID);
          w.__siteglueXPixelConfigured = true;
        }
        resolve();
      };

      if (existing) {
        finish();
        return;
      }

      const script = document.createElement("script");
      script.async = true;
      script.src = UWT_SRC;
      script.onload = () => finish();
      script.onerror = () => resolve();
      const first = document.getElementsByTagName("script")[0];
      first?.parentNode?.insertBefore(script, first);
      // Config can queue before uwt.js loads.
      if (!w.__siteglueXPixelConfigured) {
        twq("config", SITEGLUE_X_PIXEL_ID);
        w.__siteglueXPixelConfigured = true;
      }
    } catch {
      resolve();
    }
  });

  return w.__siteglueXPixelLoading;
}

export type SiteGlueXEventParams = Record<
  string,
  string | number | boolean | undefined
>;

function callTwqEvent(eventId: string, params?: SiteGlueXEventParams): void {
  const w = getTwqWindow();
  if (!w || typeof w.twq !== "function" || !eventId) return;
  const clean: Record<string, string | number | boolean> = {};
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) clean[k] = v;
    }
  }
  w.twq("event", eventId, clean);
}

function callTwqTrack(eventName: string, params?: SiteGlueXEventParams): void {
  const w = getTwqWindow();
  if (!w || typeof w.twq !== "function" || !eventName) return;
  const clean: Record<string, string | number | boolean> = {};
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) clean[k] = v;
    }
  }
  w.twq("track", eventName, clean);
}

/** Explicit page-view event (config already fires Site Visit; this hits GTM Page view if configured). */
export async function trackSiteGlueXPageView(
  params?: SiteGlueXEventParams,
): Promise<void> {
  await ensureSiteGlueXPixel();
  if (SITEGLUE_X_EVENT_PAGE_VIEW) {
    callTwqEvent(SITEGLUE_X_EVENT_PAGE_VIEW, params);
  }
}

/** Lead / signup conversion for X Ads optimization. */
export async function trackSiteGlueXLead(
  params?: SiteGlueXEventParams,
): Promise<void> {
  await ensureSiteGlueXPixel();
  if (SITEGLUE_X_EVENT_SIGN_UP) {
    callTwqEvent(SITEGLUE_X_EVENT_SIGN_UP, params);
  }
  // Standard event — still accepted by many X accounts alongside event IDs.
  callTwqTrack("Lead", params);
}

/** Purchase / hire conversion when a Purchase event ID is configured. */
export async function trackSiteGlueXPurchase(
  params?: SiteGlueXEventParams,
): Promise<void> {
  await ensureSiteGlueXPixel();
  if (SITEGLUE_X_EVENT_PURCHASE) {
    callTwqEvent(SITEGLUE_X_EVENT_PURCHASE, params);
  }
  callTwqTrack("Purchase", params);
}

/**
 * Map SiteGlue GA/GTM event names → X conversions.
 * Called from trackSiteGlueEvent so signup/hire always ping X when the pixel can load.
 */
export function trackSiteGlueXFromMarketingEvent(
  eventName: string,
  extra?: SiteGlueXEventParams,
): void {
  if (typeof window === "undefined") return;
  if (!shouldLoadSiteGlueXPixel()) return;

  const conversion = extra?.conversion === true;
  void (async () => {
    try {
      if (eventName === "sign_up_complete" || (conversion && eventName.includes("sign_up"))) {
        await trackSiteGlueXLead({ ...extra, content_name: eventName });
        return;
      }
      if (eventName === "agent_hired") {
        if (SITEGLUE_X_EVENT_PURCHASE) {
          await trackSiteGlueXPurchase({ ...extra, content_name: eventName });
        } else {
          await trackSiteGlueXLead({ ...extra, content_name: eventName });
        }
        return;
      }
      if (eventName === "paid_landing_viewed") {
        await trackSiteGlueXPageView({ ...extra, content_name: eventName });
      }
    } catch {
      // Analytics must never block UX
    }
  })();
}
